/**
 * Critical CSS for mobile-first loading
 * This CSS is inlined in the HTML head for instant rendering
 */

export const criticalMobileCSS = `
/* Critical Mobile-First CSS */
/* This CSS is inlined for instant rendering on mobile devices */

/* Reset and base styles */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  -webkit-text-size-adjust: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

body {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.5;
  color: hsl(25, 30%, 15%);
  background-color: hsl(35, 15%, 98%);
  min-height: 100vh;
  overflow-x: hidden;
}

/* Touch optimizations */
button, a, input, select, textarea {
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

/* Minimum touch targets */
button, a {
  min-height: 44px;
  min-width: 44px;
}

/* Loading state */
.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}

/* Critical layout */
.container {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Above-the-fold content */
.hero {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem 1rem;
}

/* Mobile navigation */
.nav {
  position: sticky;
  top: 0;
  z-index: 50;
  background: white;
  border-bottom: 1px solid hsl(35, 25%, 88%);
}

/* Responsive images */
img {
  max-width: 100%;
  height: auto;
  display: block;
}

/* Form elements */
input, textarea, select {
  font-size: 16px; /* Prevent iOS zoom */
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid hsl(35, 25%, 88%);
  border-radius: 0.375rem;
}

/* Buttons */
button {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
}

/* Utility classes */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.hidden {
  display: none;
}

/* Safe area insets for notched devices */
@supports (padding: max(0px)) {
  body {
    padding-left: max(0px, env(safe-area-inset-left));
    padding-right: max(0px, env(safe-area-inset-right));
    padding-bottom: max(0px, env(safe-area-inset-bottom));
  }
}

/* Disable animations on reduced motion */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
`;

/**
 * Desktop-specific CSS that can be deferred
 * This CSS is loaded after the initial render
 */
export const deferredDesktopCSS = `
/* Desktop-specific styles - loaded after initial render */
@media (min-width: 768px) {
  /* Desktop navigation */
  .nav {
    padding: 1rem 2rem;
  }
  
  /* Desktop layout */
  .container {
    padding: 0 2rem;
  }
  
  /* Desktop hero */
  .hero {
    min-height: 70vh;
    padding: 4rem 2rem;
  }
  
  /* Hover effects (desktop only) */
  button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  a:hover {
    opacity: 0.8;
  }
  
  /* Desktop grid layouts */
  .grid {
    display: grid;
    gap: 2rem;
  }
  
  .grid-cols-2 {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .grid-cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .grid-cols-4 {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1024px) {
  /* Large desktop styles */
  .container {
    padding: 0 3rem;
  }
  
  .hero {
    min-height: 80vh;
  }
}
`;

/**
 * Function to inject critical CSS into the document head
 */
export const injectCriticalCSS = () => {
  if (typeof document === 'undefined') return;
  
  const style = document.createElement('style');
  style.id = 'critical-css';
  style.textContent = criticalMobileCSS;
  document.head.insertBefore(style, document.head.firstChild);
};

/**
 * Function to load deferred desktop CSS
 */
export const loadDeferredCSS = () => {
  if (typeof document === 'undefined') return;
  
  // Only load desktop CSS on larger screens
  if (window.innerWidth < 768) return;
  
  const style = document.createElement('style');
  style.id = 'deferred-desktop-css';
  style.textContent = deferredDesktopCSS;
  style.media = 'screen and (min-width: 768px)';
  document.head.appendChild(style);
};

/**
 * Function to preload critical fonts
 */
export const preloadCriticalFonts = () => {
  if (typeof document === 'undefined') return;
  
  const fonts = [
    // Add your critical font URLs here
    // Example: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hiA.woff2'
  ];
  
  fonts.forEach(fontUrl => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.href = fontUrl;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

/**
 * Initialize mobile-first CSS loading strategy
 */
export const initMobileFirstCSS = () => {
  // Critical CSS is already inlined in HTML
  // Load deferred CSS after initial render
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      requestIdleCallback(() => {
        loadDeferredCSS();
      }, { timeout: 2000 });
    });
  } else {
    requestIdleCallback(() => {
      loadDeferredCSS();
    }, { timeout: 2000 });
  }
};

// Polyfill for requestIdleCallback
if (typeof window !== 'undefined' && !('requestIdleCallback' in window)) {
  (window as any).requestIdleCallback = (cb: IdleRequestCallback) => {
    const start = Date.now();
    return setTimeout(() => {
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, 1);
  };
}
